import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import GoogleSignInButton from "@/components/GoogleSignInButton";

export default function Login() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      nav("/discover", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col px-6 py-8">
      <button
        data-testid="login-back-btn"
        onClick={() => nav("/welcome")}
        className="flex h-10 w-10 items-center justify-center rounded-full bg-white/5 text-white/70 hover:bg-white/10"
      >
        <ArrowLeft size={18} />
      </button>

      <div className="mt-10">
        <h1 className="font-display text-4xl tracking-tight text-white">Welcome back</h1>
        <p className="mt-2 text-neutral-400">Sign in to continue your stories.</p>
      </div>

      <form onSubmit={submit} className="mt-10 space-y-4">
        <GoogleSignInButton testId="login-google-btn" />
        <div className="flex items-center gap-2 py-1">
          <div className="h-px flex-1 bg-white/10" />
          <span className="text-[10px] uppercase tracking-widest text-neutral-500">or with email</span>
          <div className="h-px flex-1 bg-white/10" />
        </div>
        <Field label="Email">
          <input
            data-testid="login-email-input"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="input-field"
          />
        </Field>
        <Field label="Password">
          <input
            data-testid="login-password-input"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input-field"
          />
        </Field>
        {error && (
          <div data-testid="login-error" className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-300">
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={loading}
          data-testid="login-submit-btn"
          className="btn-pill btn-primary w-full py-3.5 disabled:opacity-50"
        >
          {loading ? "Signing in…" : "Sign in"}
        </button>
      </form>

      <p className="mt-8 text-center text-sm text-neutral-500">
        New here?{" "}
        <Link data-testid="login-register-link" to="/register" className="text-violet-400 hover:text-violet-300">
          Create an account
        </Link>
      </p>
      <p className="mt-2 text-center text-xs text-neutral-500">
        <Link data-testid="login-forgot-link" to="/forgot-password" className="text-neutral-500 hover:text-neutral-300">
          Forgot password?
        </Link>
      </p>

      <style>{`
        .input-field {
          width: 100%;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 14px;
          padding: 0.9rem 1rem;
          color: white;
          font-family: 'Outfit', sans-serif;
          outline: none;
          transition: border-color 180ms ease, background-color 180ms ease;
        }
        .input-field:focus { border-color: rgba(139,92,246,0.5); background: rgba(255,255,255,0.06); }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs uppercase tracking-widest text-neutral-500">{label}</span>
      {children}
    </label>
  );
}
